import { useSelector } from 'react-redux';
import { createColumnHelper } from '@tanstack/react-table';
import { LocalizedText, LocalizedWallClockDate, WallClockDateService, showInfoAlert } from '@mews/framework';
import { TextKey } from '@mews-commander/enums';
import { UnicodeEntity } from '@mews/enums';
import { IconButton, IconName, Link, Stack } from '@optimus-web/core';
import { VoucherCode } from '@mews-commander/data-access';
import { TooltippedIconButton } from '@mews-commander/ui';
import { copyToClipboard } from '@mews-commander/utils';
import { getPlatformPrimaryUrl } from '../../../../../app/reducers/configuration';
import { DeleteVoucherCodesWrapper } from '../../../sections/components/vouchers/voucherCodes/DeleteVoucherCodesWrapper';

const columnHelper = createColumnHelper<VoucherCode>();

interface UseColumnsParams {
    onDeleteSuccess?: () => void;
}

export const useColumns = ({ onDeleteSuccess }: UseColumnsParams) => {
    const platformPrimaryUrl = useSelector(getPlatformPrimaryUrl);

    return [
        columnHelper.accessor('value', {
            header: () => <LocalizedText textKey={TextKey.VoucherCode} />,
        }),
        columnHelper.accessor('validityStart', {
            header: () => <LocalizedText textKey={TextKey.VoucherActiveFrom} />,
            cell: (props) => {
                const value = props.getValue();

                if (!value) {
                    return UnicodeEntity.Dash;
                }

                return <LocalizedWallClockDate wallClockDate={WallClockDateService.parse(value)} />;
            },
        }),
        columnHelper.accessor('validityEnd', {
            header: () => <LocalizedText textKey={TextKey.VoucherActiveUntil} />,
            cell: (props) => {
                const value = props.getValue();

                if (!value) {
                    return UnicodeEntity.Dash;
                }

                return <LocalizedWallClockDate wallClockDate={WallClockDateService.parse(value)} />;
            },
        }),
        columnHelper.accessor('bookingEngineUrl', {
            header: () => <LocalizedText textKey={TextKey.Url} />,
            cell: (props) => {
                const value = props.getValue();

                if (!value) {
                    return UnicodeEntity.Dash;
                }

                return (
                    <Link
                        standalone
                        variant="basic"
                        href={`${platformPrimaryUrl}${value}`}
                        target="_blank"
                        rel="noreferrer"
                    >
                        <LocalizedText textKey={TextKey.BookingEngine} />
                    </Link>
                );
            },
        }),

        columnHelper.display({
            id: 'actions',
            meta: { align: 'end' },
            cell: (props) => (
                <RowActions
                    voucherCode={props.row.original}
                    bookingUrl={`${platformPrimaryUrl}${props.row.original.bookingEngineUrl}`}
                    onSuccess={onDeleteSuccess}
                />
            ),
        }),
    ];
};

interface RowActionsInterface {
    voucherCode: VoucherCode;
    bookingUrl?: string;
    onSuccess?: () => void;
}

const RowActions = ({ voucherCode, bookingUrl, onSuccess }: RowActionsInterface) => {
    const copyLinkToClipboard = (url: string) => {
        copyToClipboard(url);

        showInfoAlert({
            description: <LocalizedText textKey={TextKey.VoucherCodeBookingUrlCopied} />,
        });
    };

    return (
        <Stack itemAlignment="center" spacing="50">
            {bookingUrl && (
                <TooltippedIconButton
                    variant="ghost"
                    icon={IconName.Copy}
                    onClick={() => copyLinkToClipboard(bookingUrl || '')}
                    size="small"
                    tooltip={<LocalizedText textKey={TextKey.VoucherCodeCopyBookingLinkToClipboard} />}
                    type="button"
                />
            )}
            <DeleteVoucherCodesWrapper
                voucherCodes={[voucherCode]}
                onSuccess={onSuccess}
                renderButtonComponent={(onClick) => (
                    <IconButton variant="ghost" icon={IconName.Delete} onClick={onClick} size="small" type="submit" />
                )}
            />
        </Stack>
    );
};
